import React, { useEffect, useState } from 'react';
import { RepairService } from '../types/BookingTypes';

interface ServiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: RepairService | null;
}

const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  isOpen,
  onClose,
  service
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;
  if (!service) return null;

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Split warranty items into arrays
  const coveredItems = service.warrantyCoveredItems.split(';');
  const notCoveredItems = service.warrantyNotCoveredItems.split(';');

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-500 ${
          isOpen ? 'bg-opacity-60' : 'bg-opacity-0'
        } z-40`}
        onClick={onClose}
      />
  
      {/* Modal */}
      <div 
        className={`fixed inset-x-0 bottom-0 z-50 transform transition-all duration-500 ease-in-out ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        onTransitionEnd={handleAnimationEnd}
      >
        <div className="bg-white rounded-t-[24px] shadow-2xl w-full max-w-lg mx-auto h-[50vh] flex flex-col overflow-hidden " style={{ height: '700px' }}>
          
          {/* Header (Sticky and always visible) */}
          <div className="bg-white sticky top-0 z-10">
            {/* Handle bar */}
            <div className="pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full opacity-80 mx-auto"/>
            </div>
  
            {/* Title Row */}
            <div className="px-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    Service Details
                  </h3>
                  <p className="text-sm text-gray-500">{service.serviceName}</p>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all duration-200"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="border-b border-gray-100" />
          </div>
  
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {/* Display Type Info */}
            {(service.serviceName?.toLowerCase().includes("display") || service.serviceName?.toLowerCase().includes("screen")) && (
              <div className="bg-yellow-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-yellow-900 mb-2">Display Type Details</h4>
                {service.serviceName?.toLowerCase().includes("oled") ? (
                  <>
                    <p className="text-sm text-yellow-800">📱 <strong>Fingerprint Sensor:</strong> Works</p>
                    <p className="text-sm text-yellow-800">🧩 <strong>Fitting Accuracy:</strong> 100%</p>
                    <p className="text-sm text-yellow-800">📦 <strong>Display Type:</strong> OLED</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-yellow-800">📱 <strong>Fingerprint Sensor:</strong> Not Supported</p>
                    <p className="text-sm text-yellow-800">🧩 <strong>Fitting Accuracy:</strong> 90% - 99%</p>
                    <p className="text-sm text-yellow-800">📦 <strong>Display Type:</strong> TFT</p>
                  </>
                )}
              </div>
            )}
  
            {/* Warranty Info */}
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <i className="fas fa-shield-alt text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">{service.warrantyName}</h4>
                  <p className="text-sm text-blue-700">{service.warrantyPeriod} Days Warranty</p>
                </div>
              </div>
              <p className="text-sm text-blue-800 mt-2">{service.warrantyTypeDescription}</p>
            </div>
  
            {/* Covered Items */}
            <div>
              <h4 className="text-base font-medium text-gray-900 mb-2">What's Covered</h4>
              <div className="space-y-2">
                {coveredItems.map((item, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-check text-green-500 w-5"></i>
                    <span>{item.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
  
            {/* Not Covered */}
            <div>
              <h4 className="text-base font-medium text-gray-900 mb-2">What's Not Covered</h4>
              <div className="space-y-2">
                {notCoveredItems.map((item, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-times text-red-500 w-5"></i>
                    <span>{item.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
  
            {/* Service Info */}
            <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-4">
              <div>
                <h5 className="text-sm font-medium text-gray-500 mb-1">Service Duration</h5>
                <p className="text-base text-gray-900">{service.serviceDuration} mins</p>
              </div>
              <div>
                <h5 className="text-sm font-medium text-gray-500 mb-1">Service Cost</h5>
                <p className="text-base text-gray-900">₹{service.serviceCost}</p>
              </div>
            </div>
          </div>
  
          {/* Footer */}
          <div className="flex-shrink-0 bg-gray-50 border-t border-gray-100 px-6 py-4">
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transform active:scale-[0.98] transition-all duration-200 shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceDetailsModal; 